import { useState, useMemo, useRef, useEffect, type ReactNode } from 'react';
import { useParams, Link as RouteLink } from 'react-router-dom';
import { useQuery, useQueryClient } from '@tanstack/react-query';
import { useTranslation } from 'react-i18next';
import { api, WORKER_API_BASE } from '../lib/api';
import { useToast } from '../components/Toast';
import { tr } from '../lib/i18nHelpers';
import {
  Loader2, CheckCircle2, XCircle, Search, Link2, MousePointerClick,
} from 'lucide-react';

// ── types ─────────────────────────────────────────────────────────────────

interface LinkRow {
  transaction_id: string;
  invoice_id: string;
  allocated_amount: number | null;
  match_status: string | null;
}

interface BundleDoc {
  id: string;
  source_file_id: string | null;
  record_type: string | null;
  record_id: string | null;
  page_start: number | null;
  page_end: number | null;
  class: string | null;
  subcover: string | null;
  title: string | null;
  status: string;
  error: string | null;
  statement?: { id: string; bank_name?: string; account_number?: string; statement_year?: number; statement_month?: number; links?: LinkRow[] } | null;
  drafts: Record<string, string | null>;
}

interface BundleRowData {
  id: string;
  status: string;
  label: string | null;
  pages_total: number | null;
  pages_done: number | null;
  docs_total: number | null;
  docs_done: number | null;
  links_proposed: number | null;
  links_confirmed: number | null;
  error: string | null;
  created_at: string | null;
}

interface TreePayload {
  bundle: BundleRowData;
  documents: BundleDoc[];
}

interface Progress extends Partial<BundleRowData> {
  status: string;
  error?: string | null;
}

// ── helpers ───────────────────────────────────────────────────────────────

const money = (v: number | null | undefined): string =>
  v == null || isNaN(Number(v)) ? '—' : Number(v).toLocaleString('en-HK', { minimumFractionDigits: 2, maximumFractionDigits: 2 });

const linksProposedDisplay = (p?: Progress, t?: TreePayload): number =>
  Number(p?.links_proposed ?? t?.bundle?.links_proposed ?? 0);

const confirmedLinksDisplay = (p?: Progress): number => Number(p?.links_confirmed ?? 0);

const DONE_STATUSES = ['ready_for_review', 'posted', 'partial', 'error'];

/**
 * Provisional journal-entry readout for a proposed link — pure helper.
 * Mirrors post-payment.ts posting shape: deposit → Dr Bank / Cr holding;
 * withdrawal → Dr holding / Cr Bank. Holding account by direction:
 *   money_in  (sales receipt)  → 11201 Trade Debtors
 *   money_out (purchase paid)  → 21101 Trade Creditors
 */
export function provisionalJeRows(
  link: { transaction_id: string; allocated_amount: number | null },
  direction: 'money_in' | 'money_out',
): { side: 'Dr' | 'Cr'; code: string; name: string; amount: number }[] {
  const amount = Number(link.allocated_amount || 0);
  const bank = { code: '11101', name: 'Cash on Hand' };
  const holding = direction === 'money_in'
    ? { code: '11201', name: 'Trade Debtors' }
    : { code: '21101', name: 'Trade Creditors' };
  return direction === 'money_in'
    ? [{ side: 'Dr', ...bank, amount }, { side: 'Cr', ...holding, amount }]
    : [{ side: 'Dr', ...holding, amount }, { side: 'Cr', ...bank, amount }];
}

function statusPillCls(status: string): string {
  switch (status) {
    case 'posted': return 'bg-green-50 text-green-700 border border-green-200';
    case 'ready_for_review': return 'bg-amber-50 text-amber-700 border border-amber-200';
    case 'partial': return 'bg-amber-50 text-amber-700 border border-amber-200';
    case 'error': return 'bg-red-50 text-red-700 border border-red-200';
    case 'intake': case 'matching': return 'bg-blue-50 text-blue-700 border border-blue-200';
    default: return 'bg-gray-100 text-gray-600 border border-gray-200';
  }
}

// ── component ─────────────────────────────────────────────────────────────

type Selection =
  | { kind: 'none' }
  | { kind: 'doc'; docId: string }
  | { kind: 'tx'; txId: string; stmtDocId: string };

export default function BundleReview() {
  useTranslation();
  const toast = useToast();
  const { id } = useParams<{ id: string }>();
  const queryClient = useQueryClient();
  const invalidate = () => {
    queryClient.invalidateQueries({ queryKey: ['bundle-tree', id] });
    queryClient.invalidateQueries({ queryKey: ['bundle-progress', id] });
    queryClient.invalidateQueries({ queryKey: ['bundles'] });
  };

  const { data: tree, isLoading: treeLoading } = useQuery<TreePayload>({
    queryKey: ['bundle-tree', id],
    queryFn: () => api(`/bundles/${id}`, { baseUrl: WORKER_API_BASE }),
    enabled: !!id,
    refetchInterval: (query: any) => {
      const st = query?.state?.data?.bundle?.status;
      return st && DONE_STATUSES.includes(st) ? false : 10_000;
    },
  });
  const { data: progress } = useQuery<Progress>({
    queryKey: ['bundle-progress', id],
    queryFn: () => api(`/bundles/${id}/progress`, { baseUrl: WORKER_API_BASE }),
    enabled: !!id,
    refetchInterval: (query: any) => {
      const st = query?.state?.data?.status;
      return st && DONE_STATUSES.includes(st) ? false : 3_000;
    },
  });

  const status = progress?.status || tree?.bundle?.status || 'queued';
  const documents: BundleDoc[] = tree?.documents || [];
  const docsById = useMemo(() => new Map(documents.map(d => [d.id, d])), [documents]);
  const docIdsWithLinks = useMemo(() => {
    const m = new Map<string, LinkRow[]>();
    for (const d of documents) {
      const rows: LinkRow[] = [];
      if (d.record_id && d.statement?.links) {
        rows.push(...d.statement.links.filter(l => l.invoice_id === d.record_id));
      }
      for (const s of documents) {
        if (s.record_type === 'statement' && s.statement?.links && d.record_id) {
          rows.push(...s.statement.links.filter(l => l.invoice_id === d.record_id));
        }
      }
      if (rows.length > 0) m.set(d.id, rows);
    }
    return m;
  }, [documents]);

  const [selection, setSelection] = useState<Selection>({ kind: 'none' });
  const selectedDoc = selection.kind === 'doc' ? docsById.get(selection.docId) || null : null;
  const selectedTx = useMemo(() => {
    if (selection.kind !== 'tx') return null;
    const stmtDoc = docsById.get(selection.stmtDocId);
    if (!stmtDoc?.statement?.links) return null;
    return stmtDoc.statement.links.filter(l => l.transaction_id === selection.txId) || [];
  }, [selection, docsById]);

  // Session notification: fire once when the bundle is first seen ready_for_review
  const seenReadyRef = useRef(false);
  useEffect(() => {
    if (status === 'ready_for_review' && !seenReadyRef.current && id) {
      seenReadyRef.current = true;
      const label = tree?.bundle?.label || '';
      (window as any).__setMatchActivity?.(
        tr(`Bundle ready for review: ${label}`, `文件批次待審核：${label}`, `文件批次待审核：${label}`),
        'done',
      );
    }
  }, [status, tree?.bundle?.label, id]);

  // ── detail-field editing: debounced 2s dirty-checked auto-draft ──────────
  type FieldType = 'draft' | 'edit';
  interface DraftState {
    values: Record<string, string>;
    lastSaved: Record<string, string>;
    savedAt: Record<string, string>;
    // fields hydrated from server-side drafts (dot shows even before edits)
    hydrated: Record<string, boolean>;
    timers: Record<string, ReturnType<typeof setTimeout>>;
  }
  const [docId, setDocId] = useState<string | null>(null);
  const [fields, setFields] = useState<DraftState>({ values: {}, lastSaved: {}, savedAt: {}, hydrated: {}, timers: {} });

  // re-seed local edit state when switching documents; drafts hydrate from the
  // tree payload (server drafts) — nothing is cached beyond the query cache.
  useEffect(() => {
    const nextId = selection.kind === 'doc' ? selection.docId : null;
    if (nextId === docId) return;
    setDocId(nextId);
    if (!nextId) { setFields({ values: {}, lastSaved: {}, savedAt: {}, hydrated: {}, timers: {} }); return; }
    const d = docsById.get(nextId);
    const seed: Record<string, string> = {};
    const hydrated: Record<string, boolean> = {};
    if (d) {
      for (const [k, v] of Object.entries(d.drafts || {})) {
        seed[k] = v == null ? '' : String(v);
        hydrated[k] = true;
      }
      seed['note'] = seed['note'] ?? '';
      hydrated['note'] = hydrated['note'] ?? false;
    }
    setFields({ values: seed, lastSaved: { ...seed }, savedAt: {}, hydrated, timers: {} });
  }, [selection, docId, docsById]);

  const setField = (docIdArg: string, field: string, type: FieldType, value: string) => {
    setFields(prev => {
      const dirty = value !== (prev.lastSaved[field] ?? '');
      const timers = { ...prev.timers };
      clearTimeout(timers[field]);
      if (!dirty) {
        delete timers[field];
        return { ...prev, values: { ...prev.values, [field]: value }, timers };
      }
      timers[field] = setTimeout(async () => {
        delete timers[field];
        try {
          if (type === 'draft') {
            await api(`/bundles/${id}/items/${docIdArg}/draft`, {
              method: 'PATCH', baseUrl: WORKER_API_BASE, body: { field, value },
            });
          } else {
            await api(`/bundles/${id}/items/${docIdArg}`, {
              method: 'PATCH', baseUrl: WORKER_API_BASE, body: { action: 'edit', [field]: value },
            });
          }
          const hhmm = new Date().toTimeString().slice(0, 5);
          setFields(p => ({
            ...p,
            lastSaved: { ...p.lastSaved, [field]: value },
            savedAt: { ...p.savedAt, [field]: hhmm },
            timers,
          }));
          invalidate();
        } catch (e: any) {
          toast.error(e?.message || tr('Save failed', '儲存失敗', '保存失败'));
        }
      }, 2000);
      return { ...prev, values: { ...prev.values, [field]: value }, timers };
    });
  };

  useEffect(() => () => { Object.values(fields.timers).forEach(clearTimeout); }, [docId]); // eslint-disable-line react-hooks/exhaustive-deps

  // ── mutations: confirm / reject / fix / post-all ─────────────────────────
  const [postingQueue, setPostingQueue] = useState<string[]>([]);
  const [summary, setSummary] = useState<{ confirmed: number; je: number; started: boolean } | null>(null);
  const bundleDone = DONE_STATUSES.includes(status);
  const pendingDocs = useMemo(
    () => documents.filter(d => d.status === 'pending'),
    [documents],
  );

  const confirmItem = async (docIdArg: string) => {
    try {
      const res: any = await api(`/bundles/${id}/items/${docIdArg}`, {
        method: 'PATCH', baseUrl: WORKER_API_BASE, body: { action: 'confirm' },
      });
      if (res?.posted) {
        setSummary(prev => ({
          confirmed: (prev?.confirmed || 0) + 1,
          je: (prev?.je || 0) + 1,
          started: true,
        }));
        toast.success(tr('Confirmed → posted server-side ✓', '已確認 → 伺服器已入帳 ✓', '已确认 → 服务器已入账 ✓'));
      } else if (res?.deferred) {
        toast.info(res?.reason || tr('Confirmed — nothing to post yet.', '已確認 — 暫無可入帳項。', '已确认 — 暂无可入账项。'));
      } else {
        toast.success(tr('Item confirmed.', '項目已確認。', '项目已确认。'));
      }
      invalidate();
    } catch (e: any) {
      toast.error(e?.message || tr('Confirm failed.', '確認失敗。', '确认失败。'));
    }
  };

  const rejectItem = async (docIdArg: string) => {
    try {
      await api(`/bundles/${id}/items/${docIdArg}`, {
        method: 'PATCH', baseUrl: WORKER_API_BASE, body: { action: 'reject' },
      });
      toast.info(tr('Link rejected · item stays unmatched.', '連結已拒絕 · 項目保持未配對。', '链接已拒绝 · 项目保持未配对。'));
      invalidate();
    } catch (e: any) {
      toast.error(e?.message || tr('Reject failed.', '拒絕失敗。', '拒绝失败。'));
    }
  };

  // ── merge / split toolbar ────────────────────────────────────────────────
  const [mergeIds, setMergeIds] = useState<Set<string>>(new Set());
  const toggleMerge = (docIdArg: string) => {
    setMergeIds(prev => {
      const next = new Set(prev);
      if (next.has(docIdArg)) next.delete(docIdArg); else next.add(docIdArg);
      return next;
    });
  };
  const doMerge = async () => {
    if (mergeIds.size < 2) { toast.warning(tr('Select 2+ documents to merge.', '揀選兩個或以上文件才能合併。', '选择两个或以上文件才能合并。')); return; }
    try {
      await api(`/bundles/${id}/fix`, {
        method: 'POST', baseUrl: WORKER_API_BASE, body: { action: 'merge', doc_ids: [...mergeIds] },
      });
      toast.success(tr('Documents merged — intake re-queued.', '已合併文件 — 重新排入收錄。', '已合并文件 — 重新排入收录。'));
      setMergeIds(new Set());
      invalidate();
    } catch (e: any) {
      toast.error(e?.message || tr('Merge failed.', '合併失敗。', '合并失败。'));
    }
  };
  const [splitPage, setSplitPage] = useState('');
  const doSplit = async (docIdArg: string) => {
    const pageNo = parseInt(splitPage, 10);
    if (!docIdArg || !Number.isInteger(pageNo)) {
      toast.warning(tr('Enter the page number where the split begins.', '輸入分割開始的頁碼。', '输入分割开始的页码。'));
      return;
    }
    try {
      await api(`/bundles/${id}/fix`, {
        method: 'POST', baseUrl: WORKER_API_BASE, body: { action: 'split', doc_id: docIdArg, page_no: pageNo },
      });
      toast.success(tr('Document split.', '已分割文件。', '已分割文件。'));
      setSplitPage('');
      invalidate();
    } catch (e: any) {
      toast.error(e?.message || tr('Split failed.', '分割失敗。', '分割失败。'));
    }
  };

  // ── confirm-queue optimizer ──────────────────────────────────────────────
  const postAllDocs = useMemo(() =>
    documents.filter(d =>
      d.status !== 'posted' &&
      d.status !== 'pending' &&
      d.status !== 'rejected' &&
      !d.error &&
      Object.keys(d.drafts || {}).length === 0 &&
      d.record_type !== 'statement' &&
      docIdsWithLinks.has(d.id)
    ), [documents, docIdsWithLinks]);

  const [postAllRunning, setPostAllRunning] = useState(false);
  const postAllCorrect = async () => {
    setPostAllRunning(true);
    let ok = 0;
    for (const d of postAllDocs) {
      setPostingQueue(prev => [...prev, d.id]);
      try {
        const res: any = await api(`/bundles/${id}/items/${d.id}`, {
          method: 'PATCH', baseUrl: WORKER_API_BASE, body: { action: 'confirm' },
        });
        if (res?.posted) ok++;
        setSummary(prev => ({
          confirmed: (prev?.confirmed || 0) + 1,
          je: (prev?.je || 0) + (res?.posted ? 1 : 0),
          started: true,
        }));
      } catch (e: any) {
        toast.error(`${d.title || d.id}: ${e?.message || tr('confirm failed', '確認失敗', '确认失败')}`);
      } finally {
        setPostingQueue(prev => prev.filter(x => x !== d.id));
      }
    }
    setPostAllRunning(false);
    if (ok > 0) {
      toast.success(tr(
        `Posted all correct (${ok}) — remaining open items stay visible.`,
        `已入帳全部正確項目（${ok}）— 其餘未決項目保持可見。`,
        `已入账全部正确项目（${ok}）— 其余未决项目保持可见。`,
      ));
      setSelection({ kind: 'none' });
      invalidate();
    }
  };

  // ── manual match search ──────────────────────────────────────────────────
  const [matchSearch, setMatchSearch] = useState('');
  // optimistic 'linked' pills for manually linked transactions — refreshed by
  // the following tree refetch (match_status flips server-side).
  const [manuallyLinked, setManuallyLinked] = useState<Set<string>>(new Set());
  const [linking, setLinking] = useState(false);
  const manualLinkTx = async (txId: string, invoiceId: string) => {
    setLinking(true);
    try {
      await api(`/bank-statements/transactions/${txId}/match`, {
        method: 'PATCH', baseUrl: WORKER_API_BASE, body: { action: 'link', invoice_id: invoiceId },
      });
      toast.success(tr('Linked ✓ — payment posted server-side.', '已連結 ✓ — 伺服器已入帳。', '已链接 ✓ — 服务器已入账。'));
      setManuallyLinked(prev => new Set(prev).add(txId));
      invalidate();
    } catch (e: any) {
      toast.error(e?.message || tr('Link failed.', '連結失敗。', '链接失败。'));
    }
    setLinking(false);
  };
  const matchCandidates = useMemo(() => {
    if (selection.kind === 'none') return [];
    if (!matchSearch.trim()) return pendingDocs.slice(0, 5);
    const q = matchSearch.toLowerCase();
    return documents.filter(d =>
      (d.title || '').toLowerCase().includes(q) ||
      (d.class || '').toLowerCase().includes(q) ||
      (d.subcover || '').toLowerCase().includes(q) ||
      (d.record_id || '').toLowerCase().includes(q)
    ).slice(0, 8);
  }, [matchSearch, documents, selection.kind]);

  // ── tree structure ───────────────────────────────────────────────────────
  interface TxGroup {
    txId: string;
    links: LinkRow[];
    matchStatus: string | null;
    stmtDocId: string;
    amount: number | null;
  }
  const stmtTxGroups: TxGroup[] = useMemo(() => {
    const out: TxGroup[] = [];
    for (const d of documents) {
      if (d.record_type !== 'statement' || !d.statement?.links) continue;
      const byTx = new Map<string, LinkRow[]>();
      for (const l of d.statement.links) {
        (byTx.get(l.transaction_id) || byTx.set(l.transaction_id, []).get(l.transaction_id)!).push(l);
      }
      for (const [txId, links] of byTx) {
        out.push({
          txId,
          links,
          matchStatus: links[0]?.match_status || null,
          stmtDocId: d.id,
          amount: links.reduce((s, l) => s + Number(l.allocated_amount || 0), 0),
        });
      }
    }
    return out;
  }, [documents]);

  const grouped = useMemo(() => {
    interface SubGroup { subcover: string | null; docs: BundleDoc[] }
    const groups: SubGroup[] = [];
    let current: SubGroup | null = null;
    for (const d of documents) {
      if (d.subcover) {
        current = { subcover: d.subcover, docs: [] };
        groups.push(current);
        current.docs.push(d);
      } else {
        current = null;
        groups.push({ subcover: null, docs: [d] });
      }
    }
    void current;
    return groups;
  }, [documents]);

  // ── pill for a document row (status pills ONLY — never tiers/confidence) ──
  function DocPill({ d }: { d: BundleDoc }) {
    if (d.error) return <span className="text-[11px] px-2 py-0.5 rounded-full bg-red-50 text-red-900 dark:text-red-200 border border-red-200">{tr('unreadable — error', '無法讀取 — 錯誤', '无法读取 — 错误')}</span>;
    if (d.status === 'posted') return <span className="text-[11px] px-2 py-0.5 rounded-full bg-green-50 text-green-700 border border-green-200">{tr('posted', '已入帳', '已入账')}</span>;
    if (d.status === 'rejected') return <span className="text-[11px] px-2 py-0.5 rounded-full bg-gray-100 text-gray-900 dark:text-gray-200 border border-gray-200">{tr('rejected', '已拒絕', '已拒绝')}</span>;
    if (d.status === 'pending' && !d.record_id) return <span className="text-[11px] px-2 py-0.5 rounded-full bg-red-50 text-red-900 dark:text-red-200 border border-red-200">{tr('unmatched — click to match manually', '未配對 — 點擊手動配對', '未配对 — 点击手动配对')}</span>;
    const links = docIdsWithLinks.get(d.id) || [];
    const confirmed = links.some(l => l.match_status === 'confirmed');
    if (confirmed) return <span className="text-[11px] px-2 py-0.5 rounded-full bg-green-50 text-green-700 border border-green-200">{tr('linked', '已連結', '已链接')}</span>;
    if (links.length > 0) return <span className="text-[11px] px-2 py-0.5 rounded-full bg-amber-50 text-amber-700 border border-amber-200">{tr('suggested · confirm or edit', '建議 · 確認或編輯', '建议 · 确认或编辑')}</span>;
    return <span className="text-[11px] px-2 py-0.5 rounded-full bg-amber-50 text-amber-700 border border-amber-200">{tr('needs review', '需審核', '需审核')}</span>;
  }

  function iconGlyph(d: BundleDoc): string {
    if (d.record_type === 'statement') return '≡';
    if (d.record_type === 'invoice') return (d.class || '').includes('purchase') ? 'P' : 'S';
    if (d.record_type === 'receipt') return 'R';
    if (d.error) return '?';
    return '▦';
  }

  function iconClsFor(d: BundleDoc): string {
    if (d.record_type === 'statement') return 'bg-blue-50 text-blue-700';
    if (d.record_type === 'invoice') return (d.class || '').includes('purchase') ? 'bg-amber-50 text-amber-700' : 'bg-green-50 text-green-700';
    if (d.record_type === 'receipt') return 'bg-purple-50 text-purple-700';
    if (d.error) return 'bg-red-50 text-red-900 dark:text-red-200';
    return 'bg-secondary text-muted-foreground';
  }

  // ── stepper ──────────────────────────────────────────────────────────────
  const stepIndex = (() => {
    switch (status) {
      case 'queued': return 0;
      case 'segmenting': return 1;
      case 'intake': return 2;
      case 'matching': return 3;
      case 'ready_for_review': return 4;
      default: return 5;
    }
  })();
  const steps: { key: string; en: string; zh: string; cn: string; sub: string }[] = [
    { key: 'uploaded', en: 'Uploaded', zh: '已上傳', cn: '已上传', sub: `${(progress?.pages_total ?? 0) || 0} ${tr('pages', '頁', '页')}` },
    { key: 'seg', en: 'Segmenting', zh: '分段', cn: '分段', sub: `${(progress?.pages_done ?? 0) || 0}/${(progress?.pages_total ?? 0) || 0} ${tr('pages', '頁', '页')}` },
    { key: 'intake', en: 'Intake', zh: '收錄', cn: '收录', sub: `${(progress?.docs_done ?? 0) || 0}/${(progress?.docs_total ?? 0) || 0} ${tr('docs', '文件', '文件')}` },
    { key: 'match', en: 'Matching', zh: '配對', cn: '配对', sub: `${(progress?.links_proposed ?? 0) || 0} ${tr('proposed', '條建議', '条建议')}` },
    { key: 'review', en: 'Review ready', zh: '待審核', cn: '待审核', sub: `${pendingDocs.length} ${tr('pending', '待處理', '待处理')}` },
    { key: 'posted', en: 'Posted', zh: '入帳', cn: '入账', sub: `${(progress?.links_confirmed ?? 0) || 0} ${tr('confirmed', '已確認', '已确认')}` },
  ];

  const jeCount = summary?.je ?? 0;

  if (treeLoading && !tree) {
    return (
      <div className="p-6 max-w-5xl mx-auto flex items-center gap-2 text-muted-foreground py-16 justify-center">
        <Loader2 className="h-5 w-5 animate-spin" /> {tr('Loading collection…', '載入文件批次…', '载入文件批次…')}
      </div>
    );
  }

  return (
    <div className="p-6 max-w-6xl mx-auto space-y-4">
      {/* header */}
      <div>
          <RouteLink to="/bundles" className="text-sm text-muted-foreground hover:text-foreground inline-flex items-center gap-1">
            <span className="text-base leading-none">←</span> {tr('Back to Collections', '返回文件批次', '返回文件批次')}
          </RouteLink>
        <h2 className="text-2xl font-bold mt-2 flex items-center gap-3 flex-wrap">
          {tree?.bundle?.label || id}
          <span className={`text-[11px] font-medium px-2 py-0.5 rounded-full ${statusPillCls(status)}`}>
            {tr(
              status.replace(/_/g, ' '),
              { queued: '排隊中', segmenting: '分段中', intake: '收錄中', matching: '配對中', ready_for_review: '待審核', posted: '已入帳', partial: '部分完成', error: '錯誤' }[status] || status.replace(/_/g, ' '),
              { queued: '排队中', segmenting: '分段中', intake: '收录中', matching: '配对中', ready_for_review: '待审核', posted: '已入账', partial: '部分完成', error: '错误' }[status] || status.replace(/_/g, ' '),
            )}
          </span>
        </h2>
        <p className="text-sm text-muted-foreground mt-1">
          {tr('Bank Document Collection', '銀行文件批次', '银行文件批次')}
          {tree?.bundle?.created_at ? ` · ${String(tree.bundle.created_at).slice(0, 16)}` : ''}
          {' · '}
          {tr('This view re-syncs from the server on every load — safe to close the page any time.', '此頁面每次載入都會與伺服器重新同步 — 可隨時關閉頁面。', '此页面每次载入都会与服务器重新同步 — 可随时关闭页面。')}
        </p>
      </div>

      {/* stepper */}
      <div className="bg-card border rounded-lg px-5 py-4 overflow-x-auto">
        <div className="flex">
          {steps.map((s, i) => {
            const done = i < stepIndex;
            const active = i === stepIndex;
            return (
              <div key={s.key} className="flex items-center min-w-[130px] flex-1 relative last:flex-none">
                {i < steps.length - 1 && (
                  <div className={`absolute top-4 left-8 right-0 h-0.5 ${done ? 'bg-green-500' : 'bg-border'}`} />
                )}
                <div className="flex items-start gap-2 relative">
                  <div className={`h-8 w-8 rounded-full flex items-center justify-center text-xs font-bold shrink-0 border-2 bg-background ${
                    done ? 'bg-green-500 border-green-500 text-white' :
                    active ? 'border-primary text-primary ring-4 ring-blue-100' : 'text-muted-foreground'}`}>
                    {done ? '✓' : i + 1}
                  </div>
                  <div className="pr-4">
                    <div className="text-xs font-semibold leading-tight">{tr(s.en, s.zh, s.cn)}</div>
                    <div className="text-[10px] text-muted-foreground leading-tight">{s.sub}</div>
                  </div>
                </div>
              </div>
            );
          })}
        </div>
      </div>

      {/* banner */}
      {(status === 'ready_for_review' || status === 'partial' || status === 'error') && (
        <div className={`rounded-lg p-3.5 text-sm flex gap-2.5 items-start ${
          status === 'error'
            ? 'bg-red-50 border border-red-200 text-red-800'
            : 'bg-blue-50 border border-blue-200 text-blue-900'}`}>
          <MousePointerClick className="h-4 w-4 mt-0.5 shrink-0" />
          <div>
            {status === 'error'
              ? <span>{tr('Processing stopped with an error.', '處理程序因錯誤停止。', '处理程序因错误停止。')} {progress?.error || tree?.bundle?.error}</span>
              : <span>{tr(
                  `${linksProposedDisplay(progress, tree)} ${pendingDocs.length} item(s) still open. Confirm each item below or post all correct ones in one go.`,
                  `${pendingDocs.length} 個項目仍未處理。請在下方逐一確認，或一次過入帳全部正確項目。`,
                  `${pendingDocs.length} 个项目仍未处理。请在下方逐一确认，或一次过入账全部正确项目。`,
                )}</span>}
          </div>
        </div>
      )}

      {/* meta strip */}
      <div className="bg-card border rounded-lg p-4">
        <div className="grid grid-cols-2 md:grid-cols-4 gap-3">
          <Meta k={tr('Bundle', '批次', '批次')} v={tree?.bundle?.label || id} />
          <Meta k={tr('Pages', '頁數', '页数')} v={`${(progress?.pages_done ?? 0) || 0}/${(progress?.pages_total ?? 0) || 0}`} />
          <Meta k={tr('Documents', '文件', '文件')} v={`${(progress?.docs_done ?? 0) || 0}/${(progress?.docs_total ?? 0) || 0}`} />
          <Meta k={tr('Links proposed', '建議連結', '建议链接')} v={String((progress?.links_proposed ?? 0) || 0)} />
          <Meta k={tr('Confirmed', '已確認', '已确认')} v={`${confirmedLinksDisplay(progress)}${summary?.started ? ` +${summary.confirmed}` : ''}`} />
          <Meta k={tr('JEs created', '已建立日記帳', '已建立日记账')} v={String(jeCount)} />
        </div>
      </div>

      {/* two columns */}
      <div className="grid lg:grid-cols-7 gap-4 items-start">
        {/* LEFT: tree */}
        <div className="lg:col-span-4 bg-card border rounded-lg p-4">
          <div className="flex items-center gap-2 mb-3 flex-wrap">
            <h3 className="text-sm font-semibold">{tr('Collection tree', '批次文件樹', '批次文件树')}</h3>
            <span className="text-xs text-muted-foreground ml-auto">
              {documents.length} {tr('documents', '份文件', '份文件')} · {(progress?.pages_total ?? 0) || 0} {tr('pages', '頁', '页')}
            </span>
            <div className="flex gap-1.5 w-full sm:w-auto">
              <button onClick={doMerge} disabled={!bundleDone}
                className="px-2 py-1 border rounded text-xs hover:bg-muted disabled:opacity-40">
                ⇔ {tr('Merge', '合併', '合并')} {mergeIds.size > 0 ? `(${mergeIds.size})` : ''}
              </button>
              <button
                onClick={() => {
                  if (selection.kind !== 'doc') { toast.warning(tr('Select a document to split.', '先揀選要分割的文件。', '先选择要分割的文件。')); return; }
                  setSplitPage(' ');
                }}
                disabled={!bundleDone}
                className="px-2 py-1 border rounded text-xs hover:bg-muted disabled:opacity-40">
                ✂ {tr('Split', '分割', '分割')}
              </button>
            </div>
          </div>

          {/* split page input (when a doc is split-selected) */}
          <SplitPrompt doc={selectedDoc} value={splitPage} setValue={setSplitPage} onConfirm={() => selection.kind === 'doc' && doSplit(selection.docId)} />

          <ul className="space-y-0.5">
            {grouped.map((g, gi) => (
              <li key={gi}>
                {g.subcover && (
                  <div className="flex items-center gap-2 px-2 py-1.5 text-xs font-semibold text-muted-foreground">
                    <span className="h-6 w-6 rounded bg-secondary flex items-center justify-center">▦</span>
                    <span>SUB-COVER — “{g.subcover}”</span>
                  </div>
                )}
                <ul className={g.subcover ? 'ml-6 border-l-2 border-border pl-2 space-y-0.5' : 'space-y-0.5'}>
                  {g.docs.map(d => {
                    const txs = stmtTxGroups.filter(t => t.stmtDocId === d.id);
                    return (
                      <li key={d.id} className="py-0.5">
                        <TreeNode
                          onClick={() => { setSelection({ kind: 'doc', docId: d.id }); }}
                          selected={selection.kind === 'doc' && selection.docId === d.id}
                          dim={d.status === 'pending'}
                          err={!!d.error}
                          icon={<span className={`h-6 w-6 rounded text-[10px] font-bold flex items-center justify-center shrink-0 ${iconClsFor(d)}`}>{iconGlyph(d)}</span>}
                          name={d.title || (d.subcover ? `SUB-COVER — “${d.subcover}”` : d.id)}
                          sub={<DocPill d={d} />}
                        />
                        {txs.length > 0 && (
                          <ul className="ml-6 border-l-2 border-border pl-2">
                            {txs.map(t => (
                              <li key={t.txId}>
                                <TreeNode
                                  onClick={() => setSelection({ kind: 'tx', txId: t.txId, stmtDocId: t.stmtDocId })}
                                  selected={selection.kind === 'tx' && selection.txId === t.txId}
                                  dim
                                  icon={<span className="h-5 w-5 rounded bg-blue-50 text-blue-700 text-[10px] font-bold flex items-center justify-center shrink-0">≡</span>}
                          name={`bank_tx ${t.txId.slice(0, 8)}${t.amount != null ? ` · ${money(t.amount)}` : ''}`}
                          sub={<TxPill matchStatus={manuallyLinked.has(t.txId) ? 'confirmed' : t.matchStatus} />}
                                />
                                {selection.kind === 'tx' && selection.txId === t.txId && (
                                  <div className="text-[10px] text-muted-foreground px-2 py-1">
                                    {t.links.map(l => (
                                      <div key={l.invoice_id}>
                                        → {docsById.get(l.invoice_id)?.title || l.invoice_id} · {money(l.allocated_amount)}
                                      </div>
                                    ))}
                                  </div>
                                )}
                              </li>
                            ))}
                          </ul>
                        )}
                      </li>
                    );
                  })}
                </ul>
              </li>
            ))}
          </ul>
        </div>

        {/* RIGHT: detail column */}
        <div className="lg:col-span-3 space-y-4">
          {/* document detail */}
          <div className="bg-card border rounded-lg p-4">
            <h3 className="text-sm font-semibold mb-3 flex items-center gap-2">
              {tr('Document detail', '文件詳情', '文件详情')}
              {selectedDoc && <span className="text-xs text-muted-foreground font-normal">{selectedDoc.title || selectedDoc.id}</span>}
            </h3>

            {!selectedDoc ? (
              <div className="text-xs text-muted-foreground py-6 text-center">
                {tr('Select a document in the tree to review its fields.', '在左邊樹中揀選文件查看詳情。', '在左边树中选择文件查看详情。')}
              </div>
            ) : (
              <div className="space-y-1">
                <FieldRow label={tr('Title', '標題', '标题')}
                  value={fields.values['title'] ?? selectedDoc.title ?? ''} savedText={fields.savedAt['title']}
                  isDirty={(fields.values['title'] ?? '') !== (fields.lastSaved['title'] ?? '')}
                  isHydrated={!!fields.hydrated['title']}
                  onChange={v => setField(selectedDoc.id, 'title', 'edit', v)} />
                <FieldRow label={tr('Type', '類型', '类型')}>
                  <select
                    value={fields.values['record_type'] ?? selectedDoc.record_type ?? ''}
                    onChange={e => setField(selectedDoc.id, 'record_type', 'edit', e.target.value)}
                    className="flex-1 border rounded px-2.5 py-1.5 text-sm bg-background min-w-0">
                    <option value="">—</option>
                    <option value="invoice">{tr('invoice', '發票', '发票')}</option>
                    <option value="receipt">{tr('receipt', '收據', '收据')}</option>
                    <option value="statement">{tr('statement', '月結單', '月结单')}</option>
                    <option value="other">{tr('other', '其他', '其他')}</option>
                  </select>
                  {(fields.values['record_type'] !== undefined && fields.values['record_type'] !== (selectedDoc.record_type ?? '')) && <DraftDot savedAt={fields.savedAt['record_type']} />}
                  {(fields.values['record_type'] === undefined || fields.values['record_type'] === (selectedDoc.record_type ?? '')) && !!fields.hydrated['record_type'] && <DraftDot savedAt={fields.savedAt['record_type']} />}
                </FieldRow>
                <FieldRow label={tr('Sub-cover', '子封面', '子封面')}
                  value={fields.values['subcover'] ?? selectedDoc.subcover ?? ''} savedText={fields.savedAt['subcover']}
                  isDirty={(fields.values['subcover'] ?? '') !== (fields.lastSaved['subcover'] ?? '')}
                  isHydrated={!!fields.hydrated['subcover']}
                  onChange={v => setField(selectedDoc.id, 'subcover', 'edit', v)} />
                <div className="flex items-center gap-2 px-2 py-0.5">
                  <div className="w-[110px] text-xs text-muted-foreground shrink-0">{tr('Pages', '頁碼', '页码')}</div>
                  <div className="text-sm">{selectedDoc.page_start ?? '—'} – {selectedDoc.page_end ?? '—'}</div>
                </div>
                <FieldRow label={tr('Note (draft)', '備註（草稿）', '备注（草稿）')}
                  value={fields.values['note'] ?? ''} savedText={fields.savedAt['note']}
                  isDirty={(fields.values['note'] ?? '') !== (fields.lastSaved['note'] ?? '')}
                  isHydrated={!!fields.hydrated['note']}
                  onChange={v => setField(selectedDoc.id, 'note', 'draft', v)} />
                {Object.keys(fields.values).filter(k => k !== 'note').length > 0 && (
                  <div className="pt-2 mt-1 border-t space-y-0.5">
                    {Object.keys(fields.values).filter(k => k !== 'note').map(k => (
                      <FieldRow key={k} label={k}
                        value={fields.values[k]} savedText={fields.savedAt[k]}
                        isDirty={(fields.values[k] ?? '') !== (fields.lastSaved[k] ?? '')}
                        isHydrated={!!fields.hydrated[k]}
                        onChange={v => setField(selectedDoc.id, k, 'draft', v)} />
                    ))}
                  </div>
                )}
                {selectedDoc.error && (
                  <div className="text-xs text-red-900 dark:text-red-200 px-2">{tr('Error:', '錯誤：', '错误：')} {selectedDoc.error}</div>
                )}

                {/* proposed link / provisional JE */}
                {(docIdsWithLinks.get(selectedDoc.id)?.length || 0) > 0 && (
                  <div className="mt-3">
                    <div className="text-xs text-muted-foreground mb-1">{tr('Proposed link', '建議連結', '建议链接')}</div>
                    <pre className="font-mono text-[11px] bg-gray-900 text-gray-200 rounded-lg p-3 leading-relaxed overflow-x-auto">
                      {(() => {
                        const links = docIdsWithLinks.get(selectedDoc.id)!;
                        const dir = (selectedDoc.class || '').includes('sales') || (selectedDoc.class || '').includes('receipt')
                          ? 'money_in' : 'money_out';
                        const lines: string[] = [];
                        for (const l of links) {
                          lines.push(
                            ...provisionalJeRows(l, dir as 'money_in' | 'money_out').map(r =>
                              `${r.side === 'Dr' ? 'Dr' : 'Cr'} ${r.code} ${r.name.padEnd(20)} ${money(r.amount)}`
                            ),
                          );
                          lines.push(`# bank_tx ${l.transaction_id.slice(0, 8)} · tx match: ${l.match_status || tr('proposed', '建議中', '建议中')}`);
                        }
                        return lines.join('\n');
                      })()}
                    </pre>
                  </div>
                )}

                {/* actions */}
                <div className="flex gap-2 mt-3 flex-wrap">
                  <button onClick={() => confirmItem(selectedDoc.id)}
                    disabled={selectedDoc.status === 'posted' || postingQueue.includes(selectedDoc.id)}
                    className="inline-flex items-center gap-1.5 px-3 py-1.5 bg-green-800 text-white rounded text-xs font-medium hover:bg-green-900 disabled:opacity-40">
                    <CheckCircle2 className="h-3.5 w-3.5" /> {tr('Confirm & auto-post', '確認並自動入帳', '确认并自动入账')}
                  </button>
                  <button onClick={() => rejectItem(selectedDoc.id)}
                    disabled={selectedDoc.status === 'posted'}
                    className="inline-flex items-center gap-1.5 px-3 py-1.5 border rounded text-xs hover:bg-muted disabled:opacity-40">
                    <XCircle className="h-3.5 w-3.5" /> {tr('Reject', '拒絕', '拒绝')}
                  </button>
                </div>
                <p className="text-[11px] text-muted-foreground mt-1.5">
                  {tr('Confirm writes JE + link rows server-side (idempotent per item); the draft overlay then resolves.', '確認後伺服器寫入日記帳及連結（每項目冪等）；草稿標示隨後消除。', '确认后服务器写入日记账及链接（每项目幂等）；草稿标示随后消除。')}
                  {' '}
                  {/* Re-OCR: the bundles API exposes merge/split/confirm/edit only — no re-OCR endpoint exists, so the control is intentionally omitted. */}
                  {tr('(Re-OCR omitted — no server endpoint yet)', '（暫不提供重新 OCR — 伺服器暫無對應介面）', '（暂不提供重新 OCR — 服务器暂无对应接口）')}
                </p>
              </div>
            )}
          </div>

          {/* manual match card */}
          {(selection.kind === 'tx' || (selectedDoc && !selectedDoc.error && selectedDoc.status !== 'posted' && !docIdsWithLinks.has(selectedDoc.id))) && (
            <div className="bg-card border rounded-lg p-4">
              <h3 className="text-sm font-semibold mb-2">{tr('Manual match', '手動配對', '手动配对')}
                {selection.kind === 'tx' && (
                  <span className="text-xs text-muted-foreground font-normal ml-2">bank_tx {selection.txId.slice(0, 8)}</span>
                )}
              </h3>
              <div className="flex items-center gap-2 mb-2">
                <Search className="h-3.5 w-3.5 text-muted-foreground shrink-0" />
                <input value={matchSearch} onChange={e => setMatchSearch(e.target.value)}
                  placeholder={tr('Search invoices / bills / receipts in this bundle…', '搜尋批次內的發票、帳單或收據…', '搜索批次内的发票、账单或收据…')}
                  className="flex-1 border rounded px-2.5 py-1.5 text-sm bg-background min-w-0" />
              </div>
              <div className="space-y-1.5 max-h-48 overflow-y-auto">
                {matchCandidates.map(c => {
                  const linkableTx = selection.kind === 'tx' ? selection.txId : null;
                  return (
                    <div key={c.id} className="flex items-center justify-between gap-2 border rounded px-2.5 py-1.5">
                      <div className="min-w-0">
                        <div className="text-[13px] font-semibold truncate">{c.title || c.id}</div>
                        <div className="text-[10px] text-muted-foreground">
                          {tr('pages', '頁', '页')} {c.page_start ?? '—'}–{c.page_end ?? '—'} · {c.class || c.record_type || tr('unknown', '未知', '未知')}
                        </div>
                      </div>
                      {c.record_id && linkableTx ? (
                        <button onClick={() => manualLinkTx(linkableTx, c.record_id!)} disabled={linking}
                          className="shrink-0 inline-flex items-center gap-1 px-2 py-1 border rounded text-xs hover:bg-muted disabled:opacity-40">
                          <Link2 className="h-3 w-3" /> {tr('Link', '連結', '链接')}
                        </button>
                      ) : (
                        <span className="shrink-0 max-w-[90px] inline-block px-1.5 py-1 border rounded text-xs text-muted-foreground/60"
                          title={c.record_id ? undefined : tr('Not parsed yet', '尚未解析', '尚未解析')}>
                          {c.record_id ? tr('Select a transaction to link', '揀選交易方可連結', '选择交易方可链接') : tr('Not parsed yet', '尚未解析', '尚未解析')}
                        </span>
                      )}
                    </div>
                  );
                })}
                {matchCandidates.length === 0 && (
                  <div className="text-xs text-muted-foreground">{tr('No candidates.', '沒有候選文件。', '没有候选文件。')}</div>
                )}
              </div>
              <div className="border border-dashed rounded px-2.5 py-2 mt-2 flex items-center justify-between gap-2">
                <span className="text-xs text-muted-foreground">{tr('No document fits? Book it manually', '沒有合適文件？手動入帳', '没有合适文件？手动入账')}</span>
                <button onClick={() => toast.info(tr(
                  'Manual posting follows the Bank Statements → manual-booking flow (embedded modal deferred).',
                  '手動入帳沿用銀行月結單的手動入帳流程（內嵌彈窗暫緩）。',
                  '手动入账沿用银行月结单的手动入账流程（内嵌弹窗暂缓）。',
                ))}
                  className="shrink-0 px-2 py-1 border rounded text-xs hover:bg-muted">
                  {tr('Manual posting…', '手動入帳…', '手动入账…')}
                </button>
              </div>
            </div>
          )}

          {/* confirm queue optimizer */}
          <div className="bg-card border rounded-lg p-4">
            <h3 className="text-sm font-semibold mb-2">{tr('Confirm queue optimizer', '確認佇列優化器', '确认队列优化器')}</h3>
            <p className="text-xs text-muted-foreground mb-2">
              {tr('Documents with parsed content, a proposed link and no draft corrections:', '已解析、有建議連結且沒有草稿修改的文件：', '已解析、有建议链接且没有草稿修改的文件：')}
            </p>
            <div className="flex flex-wrap gap-1.5">
              {postAllDocs.map(d => (
                <span key={d.id}
                  onClick={() => setSelection({ kind: 'doc', docId: d.id })}
                  className={`text-[11px] px-2 py-0.5 rounded-full bg-green-50 text-green-700 border border-green-200 cursor-pointer ${postingQueue.includes(d.id) ? 'opacity-40' : ''}`}>
                  {d.title || d.record_id || d.id}
                </span>
              ))}
              {postAllDocs.length === 0 && (
                <span className="text-xs text-muted-foreground">{tr('Nothing queued — all clear.', '目前沒有可一次入帳的項目。', '目前没有可一次入账的项目。')}</span>
              )}
            </div>
            <div className="mt-3">
              <button onClick={postAllCorrect} disabled={postAllRunning || postAllDocs.length === 0 || !bundleDone}
                className="inline-flex items-center gap-1.5 px-3 py-1.5 bg-primary text-primary-foreground rounded text-xs font-medium hover:opacity-90 disabled:opacity-40">
                {postAllRunning && <Loader2 className="h-3.5 w-3.5 animate-spin" />}
                {tr(`Post all correct (${postAllDocs.length})`, `一次過入帳全部正確項目（${postAllDocs.length}）`, `一次过入账全部正确项目（${postAllDocs.length}）`)}
              </button>
            </div>
          </div>

          {/* posting summary */}
          <div className="bg-card border rounded-lg p-4">
            <h3 className="text-sm font-semibold mb-3">{tr('Posting summary (live)', '入帳摘要（即時）', '入账摘要（即时）')}</h3>
            <div className="grid grid-cols-2 gap-3">
              <Meta k={tr('Confirmed', '已確認', '已确认')} v={`${confirmedLinksDisplay(progress)}${summary?.started ? ' → updating…' : ''}`} />
              <Meta k={tr('JEs created (this session)', '今次工作階段建立日記帳', '本次工作阶段建立日记账')} v={String(jeCount)} />
            </div>
          </div>
        </div>
      </div>
    </div>
  );
}

// ── small pieces ──────────────────────────────────────────────────────────

function TxPill({ matchStatus }: { matchStatus: string | null }) {
  if (matchStatus === 'confirmed') {
    return <span className="text-[11px] px-2 py-0.5 rounded-full bg-green-50 text-green-700 border border-green-200">{tr('linked', '已連結', '已链接')}</span>;
  }
  if (matchStatus === 'suggested') {
    return <span className="text-[11px] px-2 py-0.5 rounded-full bg-amber-50 text-amber-700 border border-amber-200">{tr('suggested · confirm or edit', '建議 · 確認或編輯', '建议 · 确认或编辑')}</span>;
  }
  return <span className="text-[11px] px-2 py-0.5 rounded-full bg-red-50 text-red-900 dark:text-red-200 border border-red-200">{tr('unmatched — click to match manually', '未配對 — 點擊手動配對', '未配对 — 点击手动配对')}</span>;
}

function Meta({ k, v }: { k: string; v: ReactNode }) {
  return (
    <div>
      <div className="text-[10px] uppercase tracking-wide text-muted-foreground">{k}</div>
      <div className="text-sm font-semibold mt-0.5 truncate" title={typeof v === 'string' ? v : undefined}>{v}</div>
    </div>
  );
}

function DraftDot({ savedAt }: { savedAt?: string }) {
  return (
    <span title={savedAt ? `draft · saved ${savedAt}` : tr('draft · saving', '草稿 · 儲存中', '草稿 · 保存中')}
      className="h-2 w-2 rounded-full bg-amber-500 shrink-0 ring-2 ring-amber-100" />
  );
}

function FieldRow({ label, children, value, onChange, savedText, isDirty, isHydrated }: {
  label: ReactNode;
  children?: ReactNode;
  value?: string;
  onChange?: (v: string) => void;
  savedText?: string;
  isDirty?: boolean;
  isHydrated?: boolean;
}) {
  return (
    <div className="flex items-center gap-2 px-2 py-0.5 rounded hover:bg-muted/60">
      <div className="w-[110px] text-xs text-muted-foreground shrink-0">{label}</div>
      {children !== undefined ? (
        children
      ) : (
        <input
          value={value || ''}
          onChange={e => onChange?.(e.target.value)}
          className="flex-1 border rounded px-2.5 py-1.5 text-sm bg-background min-w-0"
        />
      )}
      {(isDirty || isHydrated || savedText) && <DraftDot savedAt={savedText} />}
    </div>
  );
}

function TreeNode({ icon, name, sub, selected, err, dim, onClick }: {
  icon: ReactNode;
  name: ReactNode;
  sub?: ReactNode;
  selected?: boolean;
  err?: boolean;
  dim?: boolean;
  onClick?: () => void;
}) {
  return (
    <div onClick={onClick}
      className={`flex items-center gap-2 px-2 py-1.5 rounded cursor-pointer border border-transparent min-w-0 ${
        selected ? 'bg-blue-50 border-blue-200' : 'hover:bg-muted'
      } ${err ? 'bg-red-50' : ''} ${dim ? 'opacity-70' : ''}`}>
      {icon}
      <div className="flex-1 min-w-0">
        <div className="text-[13px] font-semibold truncate">{name}</div>
        {sub && <div className="text-[10px] text-muted-foreground flex items-center gap-1 flex-wrap">{sub}</div>}
      </div>
    </div>
  );
}

function SplitPrompt({ doc, value, setValue, onConfirm }: {
  doc: BundleDoc | null;
  value: string;
  setValue: (v: string) => void;
  onConfirm: () => void;
}) {
  if (!doc || value === '') return null;
  return (
    <div className="flex items-center gap-2 bg-amber-50 border border-amber-200 rounded px-2.5 py-1.5 mb-2 text-xs">
      <span>{tr('Split “%1” — first page of the new document:', '分割「%1」— 新文件首頁頁碼：', '分割「%1」— 新文件首页页码：').replace('%1', doc.title || doc.id)}</span>
      <input value={value} onChange={e => setValue(e.target.value.replace(/\D/g, ''))}
        className="w-16 border rounded px-2 py-1 bg-background" />
      <button onClick={onConfirm} className="px-2 py-1 bg-amber-900 text-white rounded hover:bg-amber-800">
        {tr('Split', '分割', '分割')}
      </button>
      <button onClick={() => setValue('')} className="px-2 py-1 border rounded hover:bg-muted">
        {tr('Cancel', '取消', '取消')}
      </button>
    </div>
  );
}
